import React, { useState, useEffect } from 'react';
import './FileList.css';
import { getScheduleFiles, getFileContent } from './fileService';

const FileList = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  // Fetch the list of files when the component mounts
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        const fileList = await getScheduleFiles();
        setFiles(fileList);
        setError('');
      } catch (err) {
        setError('Failed to load files. Please try again.');
        console.error('Error loading files:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  // Handle file selection
  const handleFileSelect = async (filename) => {
    if (selectedFile === filename && fileContent) {
      // If the same file is clicked again, just toggle the view
      setSelectedFile(null);
      setFileContent(null);
      return;
    }

    try {
      setSelectedFile(filename);
      setContentLoading(true);
      const content = await getFileContent(filename);
      setFileContent(content);
      setError('');
    } catch (err) {
      setError(`Failed to load file content for ${filename}`);
      console.error('Error loading file content:', err);
      setFileContent(null);
    } finally {
      setContentLoading(false);
    }
  };

  // Format the timestamp from the filename
  const formatTimestamp = (filename) => {
    if (!filename || !filename.includes('schedule-')) return '';
    
    const timestampPart = filename.replace('schedule-', '').replace('.json', '');
    
    if (timestampPart === 'initial') return 'Initial Schedule';
    
    try {
      // Parse the timestamp format (assuming it's a Unix timestamp in milliseconds)
      const timestamp = new Date(parseInt(timestampPart));
      return timestamp.toLocaleString();
    } catch (e) {
      return timestampPart;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="file-list-container">
        <div className="loading-message">Loading files...</div>
      </div>
    );
  }

  // Render error state
  if (error && files.length === 0) {
    // Check if it's a permission error
    const isPermissionError = error.includes('permission') || error.toLowerCase().includes('unauthorized');
    
    return (
      <div className="file-list-container">
        <div className="error-message">
          {isPermissionError 
            ? "You don't have permission to access the schedule files. Please check your authentication."
            : error}
        </div>
        
        {isPermissionError && (
          <div className="permission-help">
            <p>To fix this issue, please check your authentication and try logging in again.</p>
            <p>If the problem persists, contact your system administrator.</p>
          </div>
        )}
        
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="file-list-container">
      <h1>Schedule JSON Files</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="file-list">
        {files.length === 0 ? (
          <div className="no-files-message">No schedule files found.</div>
        ) : (
          files.map((file) => (
            <div 
              key={file.name}
              className={`file-item ${selectedFile === file.name ? 'selected' : ''}`}
              onClick={() => handleFileSelect(file.name)}
            >
              <div className="file-name">{file.name}</div>
              <div className="file-date">{formatTimestamp(file.name)}</div>
            </div>
          ))
        )}
      </div>

      {selectedFile && (
        <div className="file-content-container">
          <h2>File Content: {selectedFile}</h2>
          
          {contentLoading ? (
            <div className="loading-message">Loading content...</div>
          ) : fileContent ? (
            <pre className="file-content">
              {JSON.stringify(fileContent, null, 2)}
            </pre>
          ) : (
            <div className="error-message">Failed to load file content.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileList;
